import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { getBatchedRouteCoordinates, haveStepsChanged, getTripMapPadding } from 'src/utils';
import { getTransportProfile } from 'src/data';
import { lineString, length } from '@turf/turf';

export default function useRouteLayer({
  mapRef,
  steps,
  transportMode,
  setLoadingRoute,
  setTotalTripKms,
  setTotalTripTime,
  routeRef,
  isMobile,
  mobileDrawerHeight,
  mobileDrawerState,
  isDesktopViewExpanded,
  isPublicPortal,
  isMapLoaded,
  halfDrawerHeightRef
}) {
    const previousStepsRef = useRef();
    const hasCenteredOnceRef = useRef(false);

    useEffect(() => {
        if (!isMapLoaded) return;

        // Remove existing route layers and sources — but we need to remove all route layers from previous runs
        // So, remove any route layers/sources that start with 'route-' (or a similar prefix)
        const map = mapRef.current;
        if (!map) return;

        if (!haveStepsChanged(steps, previousStepsRef.current) && !isPublicPortal) return;

        previousStepsRef.current = steps;

        // Remove all layers and sources starting with 'route'
        const layers = map.getStyle().layers || [];
        layers.forEach(layer => {
        if (layer.id.startsWith('route')) {
            if (map.getLayer(layer.id)) map.removeLayer(layer.id);
            if (map.getSource(layer.id)) map.removeSource(layer.id);
        }
        });

        if (!steps || steps.length === 0) {
        map.easeTo({
            center: [-85.07909, 9.84913],
            zoom: isMobile ? 0.8 : 2,
            duration: 1000
        });
        return;
        }

        async function drawRoutes() {
            setLoadingRoute(true);

            try {
                let totalKms = 0;
                let totalHours = 0;

                // Check if steps is array of arrays (multiple trips)
                if (Array.isArray(steps[0])) {
                // Multiple trips: draw multiple routes
                for (let i = 0; i < steps.length; i++) {
                    const tripSteps = steps[i];
                    if (!tripSteps || tripSteps.length < 2) continue;

                    const route = await getBatchedRouteCoordinates(tripSteps, getTransportProfile(transportMode));
                    totalKms += length(lineString(route.coordinates), { units: 'kilometers' });
                    totalHours += route.durationHours;

                    const routeGeoJson = {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: route.coordinates
                        }
                    };

                    map.addSource(`route-${i}`, {
                        type: 'geojson',
                        data: routeGeoJson
                    });

                    map.addLayer({
                        id: `route-${i}`,
                        type: 'line',
                        source: `route-${i}`,
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round'
                        },
                        paint: {
                            'line-color': '#0077ff',
                            'line-width': 4,
                            'line-opacity': 0.9
                        }
                    });
                }
                } else {
                    // Single trip: draw one route as before
                    if (steps.length < 2) return;

                    const route = await getBatchedRouteCoordinates(steps, getTransportProfile(transportMode));
                    routeRef.current = route;
                    
                    totalKms = length(lineString(route.coordinates), { units: 'kilometers' });
                    totalHours = route.durationHours;     
                    
                    const routeGeoJson = {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: route.coordinates
                        }
                    };

                    // Logging for Routes Visualisation 
                    //console.log(route.coordinates.length);
                    //console.log(route.coordinates);

                    map.addSource('route', {
                        type: 'geojson',
                        data: routeGeoJson
                    });

                    map.addLayer({
                        id: 'route',
                        type: 'line',
                        source: 'route',
                        layout: {
                            'line-join': 'round',
                            'line-cap': 'round'
                        },
                        paint: {
                            'line-color': '#0077ff',
                            'line-width': 4,
                            'line-opacity': 0.9
                        }
                    });
                }

                setTotalTripKms(totalKms);
                setTotalTripTime(totalHours);

            } catch (error) {
                console.error("Failed to draw route:", error);
            } finally {
                setLoadingRoute(false);
            }
        }

        drawRoutes();

        if (!hasCenteredOnceRef.current && isPublicPortal) {
            hasCenteredOnceRef.current = true;
            //return; // Skip fitBounds on first load
        }

        // Build bounds for all steps across trips, flattening only for bounds calc
        const bounds = new mapboxgl.LngLatBounds();
        if (Array.isArray(steps[0])) {
            steps.flat().forEach(step => bounds.extend(step.point.coordinates));
        } else {
            steps.forEach(step => bounds.extend(step.point.coordinates));
        }

        setTimeout(() => {
            if (!mapRef.current) return;

            const padding = getTripMapPadding(isMobile, halfDrawerHeightRef, mobileDrawerHeight, mobileDrawerState, isDesktopViewExpanded);

            mapRef.current.fitBounds(bounds, { padding });
        }, 300);

    }, [steps, isMapLoaded]);
}
